// Decisions for the magic-moment screen (ENG-3796).
//
// Pulled out of the screen for the usual reason: inside the component these
// were reachable only by mounting a LiveKit viewer against a real camera, so
// none of them were tested — and two of them had already been wrong in ways
// that told users their onboarding succeeded when it had not.
//
// Per rules: ai-code-requires-tests, code-comments-required,
// reference-rules-in-comments (ai.knowledge/global).

/** A pan/tilt/zoom intent, as the D-pad emits it. */
export interface MoveIntent {
  readonly pan?: number | undefined;
  readonly tilt?: number | undefined;
  readonly zoom?: number | undefined;
}

/**
 * Did the user actually ask the camera to move?
 *
 * The D-pad emits an intent on release as well as on press, and a release is
 * all zeroes. Counting those would make "first PTZ action" fire for a user who
 * brushed the pad and produced no motion at all.
 */
export const isRealMovement = (intent: MoveIntent): boolean =>
  (intent.pan ?? 0) !== 0 || (intent.tilt ?? 0) !== 0 || (intent.zoom ?? 0) !== 0;

/**
 * Should the screen show its no-video state?
 *
 * DERIVED, never latched. A frame that arrives after the window expires clears
 * this on its own and the user gets their magic moment after all — latching it
 * would strand them on a failure screen next to working video.
 */
export const isVideoFailed = (input: {
  readonly waitExpired: boolean;
  readonly videoReady: boolean;
}): boolean => input.waitExpired && !input.videoReady;

/** What the coach mark says, or none. */
export type CoachMark = 'locked' | 'invite' | 'sent' | 'none';

/**
 * Which coach mark to show.
 *
 * The invite must not appear while the pad is disabled: asking a user to drag a
 * control that cannot accept input reads as the app being broken. The locked
 * message says what is actually blocking instead.
 */
export const selectCoachMark = (input: {
  readonly videoFailed: boolean;
  readonly hasSentMovement: boolean;
  readonly celebrating: boolean;
  /** False when this camera has no controls, or the user opted out of them. */
  readonly controllable?: boolean | undefined;
  /** False while the pad is disabled: still connecting, or video stopped. */
  readonly padEnabled?: boolean | undefined;
}): CoachMark => {
  // Nothing to drive: no pad is rendered, so an invitation to use one would
  // point at a control that is not on screen.
  if (input.controllable === false) return 'none';
  if (input.videoFailed) return 'locked';
  if (!input.hasSentMovement) return input.padEnabled === false ? 'locked' : 'invite';

  return input.celebrating ? 'none' : 'sent';
};

/**
 * On the way out, is this a terminal no-video failure worth reporting?
 *
 * Only when the wait genuinely expired with no frame. A user who clicks Done
 * early is abandoning, not hitting a camera fault, and counting that as an
 * error inflates the error rate with people who simply moved on. The funnel
 * already sees those as magic_moment_reached with no first_stream.
 */
export const shouldReportNoVideo = (input: {
  readonly videoVerified: boolean;
  readonly waitExpired: boolean;
  readonly alreadyReported: boolean;
}): boolean => !input.videoVerified && input.waitExpired && !input.alreadyReported;
